<template>
  <!-- TODO(alicja): extract out all those Intls -->
  <div class="bg-gray-brand-100 pb-10" :style="computedColors">
    <Loading v-if="loading" class="mt-16" />

    <!-- navbar -->
    <div class="fixed w-full top-0 z-10">
      <div class="w-full">
        <MerchantNav :disable-hide-on-scroll="true" :empty-middle="true">
          <ProgressPills :pills="progressPills" />
        </MerchantNav>
      </div>
    </div>

    <!-- main body -->
    <div v-if="!loading" class="w-full max-w-5xl mx-auto py-20">
      <!-- Edit Cart button above columns -->
      <div v-show="!hasQuestions" class="px-4 md:px-6 mb-8">
        <SecondaryButton @click="goToCart()" class="rounded-full" :size="'md'">
          <template #left-icon>
            <svg
              class="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
          </template>
          Edit Cart
        </SecondaryButton>
      </div>
      <div class="flex flex-col md:flex-row md:space-x-8 relative">
        <!-- Left column - Main checkout content -->
        <div class="md:w-[60%] px-4 md:px-6">
          <!-- questions -->
          <div
            v-for="question in checkout_questions"
            :key="question.id"
            class="mb-8 bg-white rounded-lg shadow-sm p-6"
            :class="{ 'mt-4': question?.attributes?.question || question?.attributes?.secondary_info }"
          >
            <div class="flex justify-start items-center">
              <div class="text-xl font-bold text-gray-brand-900 tracking-tighter">
                {{ question?.attributes?.question }}
              </div>
            </div>
            <div v-if="question?.attributes?.secondary_info" class="flex justify-start items-center">
              <h3 class="text-md text-gray-brand-700 w-full">
                {{ question?.attributes?.secondary_info }}
              </h3>
            </div>
            <QuestionRow :question="question" :editable="true" />
          </div>

          <div v-show="!hasQuestions">
            <InfoBanner :bottle="bottle" class="mb-8" />

            <div v-if="requiresFulfillmentSlot" id="fulfillmentModule" class="mb-8">
              <div class="mb-4">
                <div class="flex justify-start items-center">
                  <div
                    class="text-xl bg-gray-brand-300 font-bold text-gray-brand-700 w-10 h-10 p-1 flex justify-center items-center rounded-lg"
                  >
                    {{ numberedIndex('fulfillment') }}
                  </div>
                  <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter ml-3">
                    <span v-if="hasBothFulfillmentOptions">Pickup or delivery</span>
                    <span v-else-if="hasPickupOptions">Choose pickup</span>
                    <span v-else>Choose delivery</span>
                  </div>
                </div>
              </div>

              <!-- selection module -->
              <div class="bg-white rounded-lg shadow-sm p-6" v-if="showSelectionFulfillmentModule(bottle)">
                <FulfillmentSelectionModule
                  v-if="showSelectionFulfillmentModule(bottle)"
                  :fulfillment-options-section="fulfillmentSelectionModuleTab"
                  :is-loading="fulfillmentIsLoading"
                  :passed-bottle="bottle"
                  @clicked-new-method="clickedNewMethod"
                  @show-loading="fulfillmentShowLoading"
                  @delivery-address-edited-and-selected="deliveryAddressEditedAndSelected"
                  @delivery-address-action-executed="deliveryAddressActionExecuted"
                />
              </div>
              <!-- selected module -->
              <FulfillmentSelectedModule
                v-if="showSelectedFulfillmentModuleForBottle(bottle)"
                layout="checkout"
                class="mt-4"
                :is-loading="fulfillmentIsLoading"
                @select-new-fulfillment-option="selectNewFulfillmentOption"
                @show-loading="fulfillmentShowLoading"
              />

              <div v-if="isMultibottleFunctionality()">
                <FulfillmentSelectedModule
                  v-for="bottle in bottles"
                  :key="bottle.id"
                  layout="checkout"
                  class="mt-4"
                  :is-loading="fulfillmentIsLoading"
                  :passed-bottle="bottle"
                  @select-new-fulfillment-option="selectNewFulfillmentOption"
                  @show-loading="fulfillmentShowLoading"
                />
              </div>
            </div>

            <div v-if="requiresMembershipTierSelection" class="mb-8">
              <div class="mb-4">
                <div class="flex justify-start items-center">
                  <div
                    class="text-xl bg-gray-brand-300 font-bold text-gray-brand-700 w-10 h-10 p-1 flex justify-center items-center rounded-lg"
                  >
                    {{ numberedIndex('membership') }}
                  </div>
                  <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter ml-3">Membership</div>
                </div>
              </div>
              <TemplateMembership
                :show-tier="true"
                :show-list="true"
                :show-store="false"
                :show-autocharge="true"
                @tier-selecting="membershipTierSelectionLoading = true"
                @tier-selected="handleMembershipTierSelected"
              />
            </div>

            <!-- Membership Pass Section -->
            <div v-if="showMembershipPass && !membershipTierSelectionLoading" class="mb-8">
              <div class="mb-4">
                <div class="flex justify-start items-center">
                  <div
                    class="text-xl bg-gray-brand-300 font-bold text-gray-brand-700 w-10 h-10 p-1 flex justify-center items-center rounded-lg"
                  >
                    {{ numberedIndex('membership') + 1 }}
                  </div>
                  <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter ml-3">
                    Save With {{ membershipPass.attributes.name }}
                  </div>
                </div>
              </div>
              <div
                class="bg-white rounded-lg shadow-solid-xs border shadow-gray-brand-600 border-gray-brand-600 overflow-hidden"
              >
                <!-- Top row with offer icon and black section -->
                <div class="flex items-center space-x-4 p-6 border-b">
                  <div class="flex flex-col items-center min-w-[60px]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="text-brand-800"
                    >
                      <path
                        d="M7.63599 5.09084C7.63943 5.08101 7.64115 5.0761 7.64207 5.07348C8.42622 2.84782 11.5738 2.84782 12.3579 5.07348C12.3589 5.0761 12.3683 5.10311 12.3872 5.15712V5.15712C12.7215 6.11276 13.6288 6.76833 14.6412 6.76833H14.8449C17.0998 6.76833 18.0261 9.66226 16.1904 10.9716V10.9716C15.3515 11.57 15.0113 12.6583 15.3516 13.6309V13.6309C16.1059 15.7872 13.6884 17.6923 11.7947 16.4145L11.4131 16.157C11.399 16.1475 11.3919 16.1427 11.3854 16.1384C10.5463 15.5797 9.45371 15.5797 8.61457 16.1384C8.60807 16.1427 8.601 16.1475 8.58687 16.157L8.22219 16.4031C6.32314 17.6846 3.89862 15.7742 4.65512 13.6117V13.6117C4.99238 12.6477 4.66314 11.5687 3.83985 10.9642V10.9642C2.03051 9.63578 2.97013 6.76833 5.21477 6.76833H5.36556C6.37392 6.76833 7.27212 6.13097 7.60509 5.17918L7.63599 5.09084Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="block text-xs mt-1 font-semibold text-brand-800">OFFER</span>
                  </div>
                  <div class="flex-1">
                    <div class="text-white rounded-xl bg-gray-900 pt-[14px] pb-[16px] px-[22px]">
                      <h3 class="font-bold text-[15px]">
                        Join now for {{ formattedPrice }}/{{ formattedBillingInterval }}*
                      </h3>
                      <ul class="mt-[8px] space-y-2">
                        <li v-for="reward in sortedMembershipPassRewards" :key="reward.id" class="flex">
                          <span class="flex-shrink-0 mt-[-2px]">✅</span>
                          <span class="text-[15px] ml-3">{{ formatReward(reward).text }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Bottom section with radio buttons -->
                <div class="flex items-center space-x-4 px-6 py-[28px] border-b">
                  <div class="flex flex-col items-center min-w-[60px]">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="text-brand-800"
                    >
                      <rect width="24" height="24" fill="white" />
                      <path
                        d="M4 12H20M20 12L17 9M20 12L17 15"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="block text-xs mt-1 font-semibold text-brand-800">JOIN</span>
                  </div>
                  <div class="flex-1 space-y-4">
                    <div class="flex items-start cursor-pointer w-full" @click="membershipSelection = 'yes'">
                      <input
                        type="radio"
                        v-model="membershipSelection"
                        value="yes"
                        name="membershipChoice"
                        class="h-4 w-4 mt-1 text-brand-600 border-gray-300 focus:ring-brand-500"
                      />
                      <label class="ml-3 cursor-pointer w-full">
                        <span class="font-medium text-gray-900">
                          Yes! I want to save {{ immediateRewardAmount }} on today's order.
                        </span>
                        <p v-if="formattedRadioDescription" class="text-gray-500 text-sm">
                          {{ formattedRadioDescription }}
                        </p>
                      </label>
                    </div>
                    <div class="flex items-start cursor-pointer w-full" @click="membershipSelection = 'no'">
                      <input
                        type="radio"
                        v-model="membershipSelection"
                        value="no"
                        name="membershipChoice"
                        class="h-4 w-4 mt-1 text-brand-600 border-gray-300 focus:ring-brand-500"
                      />
                      <label class="ml-3 cursor-pointer w-full">
                        <span class="font-medium text-gray-900">No thanks, I'll pay full price.</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Notice section -->
                <div class="w-full bg-brand-100 text-brand-800">
                  <p class="text-sm py-4 px-6 text-center">
                    * {{ formattedBillingText }}. You can manage your membership in your account settings. Cancel
                    anytime.
                    <a href="#" class="underline" @click.prevent="isTermsModalOpen = true">Read Terms and Conditions</a>
                    <span class="no-underline">.</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="mb-8">
              <div class="mb-4">
                <div class="flex justify-start items-center">
                  <div
                    class="text-xl bg-gray-brand-300 font-bold text-gray-brand-700 w-10 h-10 p-1 flex justify-center items-center rounded-lg"
                  >
                    {{ numberedIndex('payment') + (showMembershipPass && !membershipTierSelectionLoading ? 1 : 0) }}
                  </div>
                  <div
                    class="text-2xl font-bold text-gray-brand-900 tracking-tighter ml-3"
                    @click="addADifferentPaymentMethod = !addADifferentPaymentMethod"
                  >
                    Pay
                    <span
                      v-if="isTestMode"
                      class="ml-2 text-xs uppercase bg-brand-100 text-brand-900 px-1 py-1 mr-1 rounded-md shadow-sm font-manrope tracking-widest"
                    >
                      TEST MODE
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-if="!checkout"
                class="bg-white rounded-lg shadow-solid-xs border shadow-gray-brand-600 border-gray-brand-600 p-4"
              >
                Either you're missing a fulfillment slot, the store isn't live, or the order cannot be processed yet.
              </div>
              <PaymentSection
                v-if="checkout || hasLoadedPaymentBlock"
                v-show="checkout"
                :bottle="bottle"
                :checkout="checkout"
                :has-discount-code-box="true"
              />
            </div>
          </div>
        </div>

        <!-- Right column - Order Summary and Additional Info -->
        <div class="md:w-[40%] px-4 md:px-6 md:mt-[8px] md:sticky md:h-fit" style="top: 84px">
          <!-- Order Summary Section -->
          <div class="mb-8">
            <div class="relative py-5 mb-2">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center">
                <span class="bg-gray-brand-100 px-3 text-2xl font-bold text-gray-brand-900 tracking-tighter">
                  Order Summary
                </span>
              </div>
            </div>
            <OrderSummarySection :list="$store.getters.cartItems" :checkout-attributes="checkout?.attributes" />
          </div>

          <!-- Additional Info Section -->
          <div v-if="!hasQuestions && hasAnswers" class="mb-8">
            <div class="relative py-5 mb-2">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center">
                <span class="bg-gray-brand-100 px-3 text-2xl font-bold text-gray-brand-900 tracking-tighter">
                  Additional Info
                </span>
              </div>
            </div>
            <AnswersSection :bottle="bottle" :answers="answers" />
          </div>
        </div>
      </div>
    </div>

    <!-- Add this near the end of the template -->
    <Modal :is-open="isTermsModalOpen" @close="isTermsModalOpen = false">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle">
          {{ membershipPass.attributes.name }} Terms and Conditions
        </h2>
      </template>
      <template #body>
        <div class="prose prose-sm">
          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Overview</h2>
          <p>
            This document outlines the terms and conditions for the {{ membershipPass.attributes.name }} offered by
            {{ merchantName }}. By signing up for the {{ membershipPass.attributes.name }}, you agree to the following
            terms and conditions.
          </p>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Membership Details</h2>

          <ul class="list-disc pl-4 space-y-1">
            <li>
              Membership Fee: {{ formattedPrice }}/{{ formattedBillingInterval }}, billed separately from your orders.
            </li>
            <li>Renewal: Your membership will renew automatically every {{ formattedBillingInterval }}.</li>
            <li>Cancellation: You can cancel your membership at any time through your account settings.</li>
          </ul>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Benefits</h2>

          <p>As a {{ membershipPass.attributes.name }} holder, you will receive the following perks:</p>
          <ol class="list-decimal pl-4 space-y-1">
            <li v-for="reward in sortedMembershipPassRewards" :key="reward.id">
              {{ formatReward(reward).terms_and_conditions_text }}
              <ul
                v-if="formatReward(reward).type === 'AccountCreditMembershipPassReward'"
                class="list-disc pl-4 mt-1 space-y-1"
              >
                <li v-if="formatReward(reward).autoApply">This credit will automatically apply to your next order.</li>
                <li>
                  Unused credits do not roll over and will expire exactly {{ formatReward(reward).expiryDays }}
                  {{ formatReward(reward).expiryType }}{{ formatReward(reward).expiryDays > 1 ? 's' : '' }} after being
                  issued.
                </li>
              </ul>
            </li>
          </ol>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Billing and Payments</h2>
          <ul class="list-disc pl-4 space-y-1">
            <li>Your membership fee of {{ formattedPrice }} will be billed separately from your order total.</li>
            <li>The membership will renew automatically every {{ formattedBillingInterval }} unless canceled.</li>
            <li>
              If your payment method fails, you will be notified and your membership benefits will be paused until
              payment is successfully processed.
            </li>
          </ul>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Account Management</h2>
          <ul class="list-disc pl-4 space-y-1">
            <li>You can view and manage your membership in your account settings.</li>
            <li>
              If you choose to cancel your membership, your benefits will remain active until the end of your current
              billing cycle.
            </li>
            <li>You will be able to reactivate your membership at any time through your account settings.</li>
          </ul>

          <!-- Credit Issuance section -->
          <template
            v-if="sortedMembershipPassRewards.some((r) => r.attributes.type === 'AccountCreditMembershipPassReward')"
          >
            <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Credit Issuance</h2>
            <ul class="list-disc pl-4 space-y-1">
              <template v-for="reward in sortedMembershipPassRewards" :key="reward.id">
                <li v-if="reward.attributes.type === 'AccountCreditMembershipPassReward'">
                  Account Credit Amount: {{ formatReward(reward).issuance_text }}.
                </li>
              </template>
              <template v-for="reward in sortedMembershipPassRewards" :key="reward.id">
                <li
                  v-if="
                    reward.attributes.type === 'AccountCreditMembershipPassReward' &&
                    reward.attributes.expiry_value &&
                    reward.attributes.expiry_type
                  "
                >
                  Expiration: Credit will expire at the end of each {{ reward.attributes.expiry_value }}
                  {{ reward.attributes.expiry_type }}{{ reward.attributes.expiry_value > 1 ? 's' : '' }} and cannot be
                  rolled over.
                </li>
              </template>
              <li>Usage: Credit can only be applied to orders placed through your {{ merchantName }} account.</li>
            </ul>
          </template>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Cancellation Policy</h2>
          <ul class="list-disc pl-4 space-y-1">
            <li>You may cancel your membership at any time.</li>
            <li>Upon cancellation, your membership benefits will remain active until the next renewal date.</li>
            <li>Any unused account credits will expire at the end of your current billing cycle.</li>
          </ul>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Refunds</h2>
          <ul class="list-disc pl-4 space-y-1">
            <li>Membership fees are non-refundable.</li>
            <li>
              If you cancel your membership mid-cycle, you will not be refunded for the remaining
              {{
                membershipPass?.attributes?.billing_interval === 'every_two_weeks'
                  ? 'two-week period'
                  : `portion of the ${formattedBillingInterval}`
              }}.
            </li>
          </ul>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Changes to Membership Terms</h2>
          <p>
            {{ merchantName }} reserves the right to change membership terms and benefits at any time. You will be
            notified of any changes via email. Continued use of the {{ membershipPass.attributes.name }} after changes
            have been made constitutes acceptance of the updated terms.
          </p>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Communication</h2>
          <p>
            You will receive regular email notifications related to your {{ membershipPass.attributes.name }},
            including:
          </p>
          <ul class="list-disc pl-4 space-y-1">
            <li>Credit issuance reminders</li>
            <li>Renewal notices</li>
            <li>Important updates about your membership benefits</li>
            <li>You can manage your communication preferences through your account settings.</li>
          </ul>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Contact Information</h2>
          <p>
            If you have any questions about your {{ membershipPass.attributes.name }}, please contact us at
            {{ $store.getters.merchant?.attributes.email }}.
          </p>

          <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-0">Acceptance of Terms</h2>
          <p>
            By signing up for the {{ membershipPass.attributes.name }}, you acknowledge that you have read, understood,
            and agree to these terms and conditions.
          </p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
const strftime = require('strftime')

import Loading from '@/components/Molecules/Loading'

import InfoBanner from '@/components/Checkout/InfoBanner'

import FulfillmentSelectionModule from '@/components/Checkout/FulfillmentSelectionModule'
import FulfillmentSelectedModule from '@/components/Checkout/FulfillmentSelectedModule'

import TemplateMembership from '@/components/Checkout/TemplateMembership'

import InfoRow from '@/components/Checkout/InfoRow'
import QuestionRow from '@/components/Checkout/QuestionRow'
import PaymentSection from '@/components/Checkout/PaymentSection'
import TippingSection from '@/components/Checkout/TippingSection'

import OrderSummarySection from '@/components/Checkout/OrderSummarySection'
import AnswersSection from '@/components/Checkout/AnswersSection'

import { MerchantNav } from '@/components'
import ProgressPills from '@/components/Merchant/ProgressPills'

import mapRelationship from '@/utils/mapRelationship'
import mapRelationships from '@/utils/mapRelationships'

import SecondaryButton from '@/components/Molecules/SecondaryButton'
import Modal from '@/components/Molecules/Modal'

export default {
  components: {
    FulfillmentSelectionModule,
    FulfillmentSelectedModule,
    PaymentSection,
    TippingSection,
    InfoRow,
    QuestionRow,
    TemplateMembership,
    OrderSummarySection,
    AnswersSection,
    MerchantNav,
    ProgressPills,
    InfoBanner,
    Loading,
    SecondaryButton,
    Modal
  },
  data: () => ({
    token: null,
    address1: '',
    address2: '',
    city: '',
    state: 'CA',
    zip: '',
    selectNewFulfillmentMethod: false,
    fulfillmentSelectionModuleTab: '',
    loading: true,
    sections: [],
    fulfillmentIsLoading: false,
    hasLoadedPaymentBlock: false,
    selectedBottle: null,
    membershipSelection: null,
    membershipTierSelectionLoading: false,
    isTermsModalOpen: false,
    lastSelectedFulfillmentSection: null
  }),
  computed: {
    computedColors() {
      const hsl = this.$store.getters.info?.attributes.color.hsl
      if (hsl)
        return {
          '--main-brand-hue': hsl[0],
          '--main-brand-saturation': hsl[1],
          '--main-brand-lightness': hsl[2]
        }
    },
    bottle() {
      return this.$store.getters.currentBottle
    },
    bottles() {
      return this.$store.getters.currentBottles.sort((a, b) => (parseInt(a.id) > parseInt(b.id) ? 1 : -1))
    },
    onlyOneMultibottle() {
      return this.bottles.length < 2
    },
    checkout() {
      return this.$store.getters.checkout
    },
    hasQuestions() {
      return this.checkout_questions?.length > 0
    },
    hasAnswers() {
      return this.answers?.length > 0
    },
    isTestMode() {
      return this.checkout?.attributes.mode == 'test'
    },
    requiresFulfillmentSlot() {
      return this.bottle.attributes?.requires_fulfillment_slot
    },
    requiresMembershipTierSelection() {
      return this.bottle.attributes?.requires_membership_tier_selection
    },
    fulfillmentSlotID() {
      return this.bottle?.relationships?.fulfillment_slot?.data?.id
    },
    fulfillmentSlot() {
      return this.$store.getters.fulfillmentSlot
    },
    possibleFulfillmentMethods() {
      return this.$store.getters.fulfillmentMethods(this.bottle?.relationships?.possible_fulfillment_methods?.data)
    },
    hasPickupOptions() {
      // return this.possiblePickupFulfillmentMethods?.length > 0
      return this.bottle?.attributes?.possible_fulfillment_method_types?.pickup?.type == 'pickup'
    },
    hasDeliveryOptions() {
      return this.bottle?.attributes?.possible_fulfillment_method_types?.delivery?.type == 'delivery'
    },
    hasBothFulfillmentOptions() {
      return this.hasPickupOptions && this.hasDeliveryOptions
    },
    showDeliveryFulfillmentOptionsFirst() {
      return mapRelationship(this.$store.state, this.bottle?.relationships?.store?.data)?.attributes
        ?.show_delivery_fulfillment_options_first
    },
    pickupOptions() {
      return this.$store.getters.pickupFulfillmentMethods(this.bottle?.id)
    },
    deliveryOptions() {
      if (this.$store.state.objects.fulfillment_method)
        return Object.values(this.$store.state.objects.fulfillment_method).filter(
          (x) => x?.attributes.type !== 'PickupFulfillmentMethod'
        )
    },
    checkout_questions() {
      return this.$store.getters.questions(this.bottle?.relationships?.checkout_questions?.data)?.slice(0, 1)
    },
    answers() {
      return this.$store.getters.answers(this.bottle?.relationships?.answers?.data)
    },
    progressPills() {
      return [
        ...(this.bottle?.attributes?.supports_selection_screen
          ? [{ status: 'completed', to: { name: 'Selection' }, label: 'Selection' }]
          : []),
        { status: 'current', to: '', label: 'Checkout' }
      ].filter(Boolean)
    },
    membershipPass() {
      return mapRelationship(
        this.$store.state,
        this.$store.getters.membershipTier?.relationships?.membership_pass?.data
      )
    },
    membershipPassRewards() {
      return mapRelationships(this.$store.state, this.membershipPass?.relationships?.membership_pass_rewards?.data)
    },
    showMembershipPass() {
      // First check if there's a membership pass in the selected tier
      if (!this.membershipPass) return false

      // If there's no membership pass selection, show the section
      if (!this.membershipPassSelection) return true

      // Get the membership pass selection's membership pass ID and convert to integer
      const currentPassId = parseInt(this.membershipPassSelection.attributes.membership_pass_id)
      const selectedPassId = parseInt(this.membershipPass.id)

      // Only show the section if the user doesn't already have this specific membership pass
      return currentPassId !== selectedPassId
    },
    formattedBillingInterval() {
      const interval = this.membershipPass?.attributes?.billing_interval
      if (!interval) return ''

      if (interval === 'every_two_weeks') {
        return 'every two weeks'
      }

      return interval.replace(/ly$/, '')
    },
    formattedPrice() {
      const price = this.membershipPass?.attributes?.price?.format
      if (!price) return ''

      return price.endsWith('.00') ? price.slice(0, -3) : price
    },
    immediateRewardAmount() {
      if (!this.membershipPassRewards) return null

      // Try to find ImmediateMembershipPassReward first
      const immediateReward = this.membershipPassRewards.find(
        (reward) => reward.attributes.type === 'ImmediateMembershipPassReward'
      )
      if (immediateReward) {
        const amount = immediateReward.attributes.credit_amount?.format
        return amount?.endsWith('.00') ? amount.slice(0, -3) : amount
      }

      // Then try AccountCreditMembershipPassReward
      const accountCreditReward = this.membershipPassRewards.find(
        (reward) => reward.attributes.type === 'AccountCreditMembershipPassReward'
      )
      if (accountCreditReward) {
        const amount = accountCreditReward.attributes.credit_amount?.format
        return amount?.endsWith('.00') ? amount.slice(0, -3) : amount
      }

      // Finally try FulfillmentMembershipPassReward
      const fulfillmentReward = this.membershipPassRewards.find(
        (reward) => reward.attributes.type === 'FulfillmentMembershipPassReward'
      )
      if (fulfillmentReward) {
        return 'on delivery' // Since fulfillment rewards don't have an amount
      }

      return null
    },
    formattedRadioDescription() {
      if (!this.membershipPassRewards) return ''

      // Get all rewards except the one shown in the title
      let remainingRewards = [...this.membershipPassRewards]

      // Remove the first reward of the first type found (following hierarchy)
      if (remainingRewards.some((r) => r.attributes.type === 'ImmediateMembershipPassReward')) {
        const index = remainingRewards.findIndex((r) => r.attributes.type === 'ImmediateMembershipPassReward')
        remainingRewards.splice(index, 1)
      } else if (remainingRewards.some((r) => r.attributes.type === 'AccountCreditMembershipPassReward')) {
        const index = remainingRewards.findIndex((r) => r.attributes.type === 'AccountCreditMembershipPassReward')
        remainingRewards.splice(index, 1)
      } else if (remainingRewards.some((r) => r.attributes.type === 'FulfillmentMembershipPassReward')) {
        const index = remainingRewards.findIndex((r) => r.attributes.type === 'FulfillmentMembershipPassReward')
        remainingRewards.splice(index, 1)
      }

      // If no remaining rewards, return empty string
      if (remainingRewards.length === 0) return ''

      // Format remaining rewards
      const formattedRewards = remainingRewards
        .map((reward) => {
          const type = reward.attributes.type
          const amount = reward.attributes.credit_amount?.format
          const formattedAmount = amount?.endsWith('.00') ? amount.slice(0, -3) : amount

          switch (type) {
            case 'ImmediateMembershipPassReward':
              return `${formattedAmount} off this order for signing up`

            case 'AccountCreditMembershipPassReward': {
              const interval = reward.attributes.credit_interval
              const formattedInterval = interval === 'every_two_weeks' ? 'every two weeks' : interval
              return `${formattedAmount} account credit applied ${formattedInterval}`
            }

            case 'FulfillmentMembershipPassReward':
              return 'free delivery on all orders'

            default:
              return null
          }
        })
        .filter(Boolean)

      return formattedRewards.length > 0 ? `As well as get ${formattedRewards.join(' and ')}.` : ''
    },
    formattedBillingText() {
      if (!this.membershipPass) return ''

      const price = this.formattedPrice
      const interval = this.membershipPass.attributes.billing_interval

      // Format the interval to be more readable
      const formattedInterval =
        {
          weekly: 'week',
          monthly: 'month',
          yearly: 'year',
          every_two_weeks: 'two weeks'
        }[interval] || interval

      return `This membership is ${price} billed separately and will renew every ${formattedInterval}`
    },
    merchantName() {
      return this.$store.getters.merchant?.attributes.user_name
    },
    sortedMembershipPassRewards() {
      if (!this.membershipPassRewards) return []

      // Define the order of reward types
      const orderMap = {
        ImmediateMembershipPassReward: 1,
        AccountCreditMembershipPassReward: 2,
        FulfillmentMembershipPassReward: 3
      }

      // Sort rewards based on their type
      return [...this.membershipPassRewards].sort((a, b) => orderMap[a.attributes.type] - orderMap[b.attributes.type])
    },
    membershipPassSelection() {
      const conversationUserPair = mapRelationship(
        this.$store.state,
        this.bottle?.relationships?.conversation_user_pair?.data
      )
      return mapRelationship(this.$store.state, conversationUserPair?.relationships?.membership_pass_selection?.data)
    },
    pendingMembershipPassUpdate() {
      // If no selection has been made, no update needed
      if (this.membershipSelection === undefined) return false

      // If user selected 'yes', check if they already have this pass
      if (this.membershipSelection === 'yes') {
        const currentPassId = parseInt(this.membershipPassSelection?.attributes?.membership_pass_id)
        const selectedPassId = parseInt(this.membershipPass?.id)
        // Only need to update if the IDs don't match
        return currentPassId !== selectedPassId
      }

      // If user selected 'no', check if they currently have any pass
      if (this.membershipSelection === 'no') {
        // Only need to update if they currently have a pass
        return !!this.membershipPassSelection
      }

      return false
    },
    pendingMembershipPassId() {
      if (this.membershipSelection === 'yes') {
        return this.membershipPass.id
      } else if (this.membershipSelection === 'no') {
        return null
      }
      return undefined
    }
  },
  watch: {
    fulfillmentSlotID: function (newID, oldID) {
      if (newID !== null) {
        this.selectNewFulfillmentMethod = false
      }
    },
    checkout: function (newCheckout, oldCheckout) {
      if (newCheckout !== null) {
        this.hasLoadedPaymentBlock = true
      }
    },
    membershipSelection: {
      async handler(newValue) {
        if (newValue === 'yes') {
          await this.$store.dispatch('selectPass', this.membershipPass.id)
        } else if (newValue === 'no') {
          await this.$store.dispatch('selectPass', null)
        }
      }
    }
  },
  async mounted() {
    window.scrollTo(0, 0)
    this.$store.dispatch('getInfo')
    this.$store.dispatch('getMembershipTiers')
    this.$store.dispatch('getCustomerInfo')
    await this.$store.dispatch('getBottle', {
      includes: [
        'fulfillment_slot',
        'fulfillment_slot.fulfillment_method',
        'possible_fulfillment_methods',
        'possible_fulfillment_slot_days',
        'possible_fulfillment_slot_times',
        'possible_delivery_addresses',
        'possible_stripe_payment_methods',
        'possible_fulfillment_methods.delivery_address',
        'possible_fulfillment_methods.fulfillment_slots_detail',
        'checkout',
        'cart',
        'cart.cart_items',
        'cart.cart_items.product',
        'cart.cart_items.product.category_product_store_pairs',
        'cart.applied_discount_codes',
        'checkout_questions',
        'answers',
        'answers.question',
        'possible_tip_amounts',
        'membership_pass',
        'conversation_user_pair',
        'conversation_user_pair.membership_pass_selection'
      ]
    })
    this.loadSections()
    this.loading = false
    this.handleAuthorization()
    this.handleStatus()
    if (this.hasPickupOptions && !this.showDeliveryFulfillmentOptionsFirst) {
      this.fulfillmentSelectionModuleTab = ''
    } else {
      this.fulfillmentSelectionModuleTab = ''
    }
    if (this.showMembershipPass) {
      await this.$store.dispatch('getMembershipPassRewards')
    }
    // After loading the bottle and getting all necessary data
    if (this.membershipPass?.attributes?.auto_select && this.showMembershipPass) {
      this.membershipSelection = 'yes'
    }
  },
  methods: {
    fulfillmentSlotForBottle(bottle) {
      return this.$store.getters.fulfillmentSlotForBottle(bottle)
    },
    showSelectedFulfillmentModuleForBottle(bottle) {
      const slot = mapRelationship(this.$store.state, bottle?.relationships?.fulfillment_slot.data)
      const method = mapRelationship(this.$store.state, slot?.relationships?.fulfillment_method.data)

      if (!slot && !method) {
        return false
      }

      if (method?.attributes?.data?.type === 'PickupFulfillmentMethod') {
        return true
      }

      return !!bottle.relationships.delivery_address.data
    },
    fulfillmentSlotIDForBottle(bottle) {
      return bottle?.relationships?.fulfillment_slot?.data?.id
    },
    showSelectionFulfillmentModule(bottle) {
      if (
        this.selectedBottle?.id === bottle?.id ||
        !bottle.relationships.delivery_address.data ||
        this.selectNewFulfillmentMethod ||
        this.fulfillmentSlotID == null
      ) {
        return true
      } else {
        return false
      }
    },
    fulfillmentSlotForBottle(bottle) {
      return this.$store.getters.fulfillmentSlotForBottle(bottle)
    },
    isMultibottleFunctionality() {
      const currentBottle = this.$store.getters.currentBottle
      if (currentBottle) {
        return currentBottle.attributes.allow_multiple_days && !this.onlyOneMultibottle
      } else {
        return false
      }
    },
    handleAuthorization() {
      if (!this.bottle) {
        this.$router.push({ name: 'Home' })
      } else if (!this.$store.getters.ownsBottle(this.bottle)) {
        this.$router.push({ name: 'Login', params: { bottleid: this.bottle.id } })
      }
    },
    handleStatus() {
      const status = this.$store.getters.currentBottle?.attributes?.status
      if (status == 'paid') {
        this.$router.push({ name: 'Selection' })
      }
    },
    dateForBottle(bottle) {
      if (bottle.attributes.is_bottle_cycle) {
        return this.dateForBottleCycleBottle(bottle)
      } else {
        return this.dateForStoreBottle(bottle)
      }
    },
    dateForBottleCycleBottle(bottle) {
      const bottleDetails = this.bottleCycleDetailsForBottle(bottle)
      return strftime('%A, %B %-d ', new Date(bottleDetails?.fulfillment_slot_day + 'T12:00:00.000Z'))
    },
    dateForStoreBottle(bottle) {
      if (!this.fulfillmentSlotForBottle(bottle)) return undefined
      return strftime(
        '%A, %B %-d ',
        new Date(this.fulfillmentSlotForBottle(bottle).attributes.fulfillment_date + 'T12:00:00.000Z')
      )
    },
    bottleCycleDetailsForBottle(forBottle) {
      return forBottle?.attributes?.bottle_cycle_details
    },
    selectNewFulfillmentOption(option) {
      const currentMethod = this.$store.getters.fulfillmentMethod
      if (currentMethod?.attributes?.type === 'PickupFulfillmentMethod') {
        this.lastSelectedFulfillmentSection = 'pickup'
      } else if (currentMethod) {
        this.lastSelectedFulfillmentSection = 'delivery'
      }

      this.showSelectionOptionForFulfillmentType(option)
    },
    selectNewFulfillmentOptionMultibottle(option) {
      if (this.selectedBottle?.id === option['bottle']?.id) {
        this.selectedBottle = undefined
      } else {
        this.showSelectionOptionForFulfillmentType(option)
      }
    },
    showSelectionOptionForFulfillmentType(option) {
      this.selectedBottle = option['bottle']
      this.selectNewFulfillmentMethod = true
      if (option['method'] == 'pickup') {
        this.fulfillmentSelectionModuleTab = 'pickup'
      } else if (option['method'] == 'address') {
        this.fulfillmentSelectionModuleTab = 'delivery'
      }
    },
    clickedNewMethod() {
      this.selectedBottle = null
      this.selectNewFulfillmentMethod = false
    },
    loadSections() {
      if (this.requiresFulfillmentSlot) {
        this.sections.push('fulfillment')
      }
      if (this.requiresMembershipTierSelection) {
        this.sections.push('membership')
      }
      this.sections.push('payment')
    },
    numberedIndex(sectionString) {
      return this.sections.indexOf(sectionString) + 1
    },
    fulfillmentShowLoading(value) {
      this.fulfillmentIsLoading = value
    },
    deliveryAddressEditedAndSelected() {
      this.selectedBottle = undefined
      this.selectNewFulfillmentMethod = false
    },
    deliveryAddressActionExecuted() {
      this.fulfillmentSelectionModuleTab = 'delivery'
    },
    goToCart() {
      this.$router.push({ name: 'Selection', params: { bottleid: this.bottle?.id }, query: { showCart: 'y' } })
    },
    formatReward(reward) {
      if (!reward)
        return {
          type: null,
          text: '',
          terms_and_conditions_text: ''
        }

      const attrs = reward.attributes

      const formatAmount = (amount) => {
        if (!amount) return ''
        return amount.endsWith('.00') ? amount.slice(0, -3) : amount
      }

      // Helper function for ordinal suffixes
      const getOrdinalSuffix = (n) => {
        const s = ['th', 'st', 'nd', 'rd']
        const v = n % 100
        return s[(v - 20) % 10] || s[v] || s[0]
      }

      // Helper function to capitalize first letter
      const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1)
      }

      switch (attrs.type) {
        case 'ImmediateMembershipPassReward':
          return {
            type: attrs.type,
            text: `${formatAmount(attrs.credit_amount?.format)} off this order for signing up!`,
            terms_and_conditions_text: `${formatAmount(attrs.credit_amount?.format)} off your first order.`
          }

        case 'AccountCreditMembershipPassReward': {
          const amount = formatAmount(attrs.credit_amount?.format)
          const interval = attrs.credit_interval
          const awardDay = attrs.credit_award_day
          const awardDate = attrs.credit_award_date
          const expiryValue = attrs.expiry_value
          const expiryType = attrs.expiry_type
          const autoApply = attrs.auto_apply

          // Build the timing part for issuance text
          let issuanceDay = ''
          if (interval === 'monthly' && awardDate) {
            issuanceDay = `on the ${awardDate}${getOrdinalSuffix(awardDate)} of each month`
          } else if (interval === 'weekly' && awardDay) {
            issuanceDay = `every ${capitalizeFirstLetter(awardDay)}`
          } else if (interval === 'every_two_weeks' && awardDay) {
            issuanceDay = `every other ${capitalizeFirstLetter(awardDay)}`
          }

          // Build the regular timing part for other displays
          let timing = ''
          if (interval === 'monthly' && awardDate) {
            timing = `on the ${getOrdinalSuffix(awardDate)} of each month`
          } else if (interval === 'weekly' && awardDay) {
            timing = `every ${capitalizeFirstLetter(awardDay)}`
          } else if (interval === 'every_two_weeks' && awardDay) {
            timing = `every other ${capitalizeFirstLetter(awardDay)}`
          }

          // Build the expiry part of the message
          let expiry = ''
          if (expiryValue && expiryType) {
            expiry = `, expires after ${expiryValue} ${expiryType}${expiryValue > 1 ? 's' : ''}`
          }

          const autoApplyText = autoApply ? ' (automatically applied at checkout)' : ''

          return {
            type: attrs.type,
            text: `${amount} account credit ${timing}${expiry}${autoApplyText}`,
            terms_and_conditions_text: `${amount} account credit ${timing}${expiry}${autoApplyText}.`,
            issuance_text: `${amount} will be added to your account balance ${issuanceDay}`,
            autoApply,
            expiryDays: expiryValue,
            expiryType: expiryType
          }
        }

        case 'FulfillmentMembershipPassReward':
          return {
            type: attrs.type,
            text: 'Free delivery on all orders!',
            terms_and_conditions_text: 'Free delivery on all orders.'
          }

        default:
          return null
      }
    },
    getOrdinalSuffix(n) {
      const s = ['th', 'st', 'nd', 'rd']
      const v = n % 100
      return s[(v - 20) % 10] || s[v] || s[0]
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    async handleMembershipTierSelected() {
      this.membershipTierSelectionLoading = false

      // Check if there's a pass available in the selected tier

      if (this.membershipPass) {
        const selectedPassId = parseInt(this.membershipPass.id)
        const currentPassId = parseInt(this.membershipPassSelection?.attributes?.membership_pass_id)

        if (selectedPassId === currentPassId) {
          console.log('selecting original pass!!!')
          await this.$store.dispatch('selectPass', this.membershipPass.id)
          this.membershipSelection = 'yes'
          return
        }
        if (this.membershipPass.attributes.auto_select) {
          console.log('selecting pass!!!')
          await this.$store.dispatch('selectPass', this.membershipPass.id)
          this.membershipSelection = 'yes'
        }
      }
    }
  }
}
</script>
